import { lazy, Suspense } from "react";
import "@/components/task-description.css";
import {
  isLong,
  withoutLeadingHeading,
} from "@/components/task-description-text";
import { Skeleton } from "@/components/ui/loader";
import type { TaskDescriptionVariant } from "@/components/task-description";
import type { MarkdownText } from "@/lib/client/maintenance-tasks";

// The Markdown renderer is the heavy part, so it loads on the first task
// description rather than with the shell. Nothing outside this file may import
// task-description.tsx or react-markdown statically. The stylesheet stays here
// so the placeholder is sized exactly like the rendered text.
const TaskDescription = lazy(() =>
  import("@/components/task-description").then((module) => ({
    default: module.TaskDescription,
  })),
);

type Props = {
  description: MarkdownText;
  variant: TaskDescriptionVariant;
  className?: string;
};

// Shows no source text: unrendered Markdown reads as broken syntax.
function DescriptionPlaceholder({ description, variant, className }: Props) {
  const body = withoutLeadingHeading(description);
  if (!body.trim()) return null;
  const preview = variant === "preview";
  return (
    <div
      className={`task-description ${preview ? "is-preview" : "is-full"}`}
      aria-busy="true"
    >
      <div className={`task-description-placeholder ${className ?? ""}`.trim()}>
        {preview ? (
          [0, 1, 2].map((line) => (
            <Skeleton key={line} className="task-description-skeleton-line" />
          ))
        ) : (
          <Skeleton className="task-description-skeleton-block" />
        )}
      </div>
      {preview && isLong(body) ? (
        <span className="task-description-toggle-space" aria-hidden="true" />
      ) : null}
    </div>
  );
}

export function TaskDescriptionSlot(props: Props) {
  return (
    <Suspense fallback={<DescriptionPlaceholder {...props} />}>
      <TaskDescription {...props} />
    </Suspense>
  );
}
